import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import './EntradaInventario.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';
import { FaPlus, FaTrash, FaBoxOpen, FaCalendarAlt, FaClipboardList, FaEye } from 'react-icons/fa';

const EntradaInventario = () => {
  const [entradas, setEntradas] = useState([]);
  const [entradasAgrupadas, setEntradasAgrupadas] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [opcionesIngredientes, setOpcionesIngredientes] = useState([]);
  const [ingredienteSeleccionado, setIngredienteSeleccionado] = useState(null);
  const [ingredientesAgregados, setIngredientesAgregados] = useState([]);
  const [modoSeleccion, setModoSeleccion] = useState('simple'); // 'simple' o 'multiple'
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [entradaAEliminar, setEntradaAEliminar] = useState(null);
  const [entradaDetalle, setEntradaDetalle] = useState(null);

  // Campos del formulario
  const [formData, setFormData] = useState({
    cantidad: '',
    comentario: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);

  const toast = useToast();

  // Función para limitar a 3 decimales
  const limitarDecimales = (valor, decimales = 3) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return 0;
    return Math.round(num * Math.pow(10, decimales)) / Math.pow(10, decimales);
  };

  useEffect(() => {
    cargarIngredientes();
    cargarEntradas();
  }, []);

  useEffect(() => {
    cargarEntradas();
  }, [fechaInicio, fechaFin]);

  const cargarIngredientes = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getIngredientes' })
      });

      const data = await response.json();

      if (data.success) {
        setIngredientes(data.data || []);
        const opciones = (data.data || []).map(ingrediente => ({
          value: ingrediente.idIngrediente,
          label: ingrediente.nombre
        }));
        setOpcionesIngredientes(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar ingredientes:', error);
      toast.error('Error al cargar ingredientes');
    }
  };

  const cargarEntradas = async () => {
    try {
      setLoading(true);

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'getEntradasInventario',
          fechaInicio: fechaInicio,
          fechaFin: fechaFin
        })
      });

      const data = await response.json();

      if (data.success) {
        const entradasData = data.data || [];
        setEntradas(entradasData);

        // Agrupar por idEntrada
        const agrupadas = {};
        entradasData.forEach(e => {
          if (!agrupadas[e.idEntrada]) {
            agrupadas[e.idEntrada] = {
              idEntrada: e.idEntrada,
              fecha: e.fecha,
              comentario: e.comentario,
              hora: e.hora,
              ingredientes: []
            };
          }
          agrupadas[e.idEntrada].ingredientes.push({
            idEntradaDetalle: e.idEntradaDetalle,
            idIngrediente: e.idIngrediente,
            nombreIngrediente: e.nombreIngrediente,
            cantidad: e.cantidad,
            costo: e.costo
          });
        });

        setEntradasAgrupadas(Object.values(agrupadas));
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar entradas:', error);
      toast.error('Error al cargar entradas');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIngredienteChange = (selectedOption) => {
    setIngredienteSeleccionado(selectedOption);
  };

  const agregarIngrediente = () => {
    if (!ingredienteSeleccionado) {
      toast.error('Seleccione un ingrediente');
      return;
    }

    if (ingredientesAgregados.find(i => i.idIngrediente === ingredienteSeleccionado.value)) {
      toast.error('Este ingrediente ya está en la lista');
      return;
    }

    const nuevoIngrediente = {
      idIngrediente: ingredienteSeleccionado.value,
      nombre: ingredienteSeleccionado.label,
      cantidad: 1
    };

    setIngredientesAgregados([...ingredientesAgregados, nuevoIngrediente]);
    setIngredienteSeleccionado(null);
  };

  const eliminarIngredienteAgregado = (idIngrediente) => {
    setIngredientesAgregados(ingredientesAgregados.filter(i => i.idIngrediente !== idIngrediente));
  };

  const cambiarCantidadIngrediente = (idIngrediente, cantidad) => {
    setIngredientesAgregados(ingredientesAgregados.map(i =>
      i.idIngrediente === idIngrediente ? { ...i, cantidad: limitarDecimales(cantidad, 3) } : i
    ));
  };

  const nuevaEntrada = () => {
    setFormData({
      cantidad: '',
      comentario: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setIngredienteSeleccionado(null);
    setIngredientesAgregados([]);
    setModoSeleccion('simple');
    setShowModal(true);
  };

  const guardarEntrada = async () => {
    try {
      setGuardando(true);

      if (modoSeleccion === 'simple') {
        if (!ingredienteSeleccionado) {
          toast.error('Debe seleccionar un ingrediente');
          return;
        }

        if (!formData.cantidad || parseFloat(formData.cantidad) <= 0) {
          toast.error('La cantidad debe ser mayor a 0');
          return;
        }

        const payload = {
          tipo: 'crearEntradaInventario',
          idIngrediente: ingredienteSeleccionado.value,
          cantidad: formData.cantidad,
          comentario: formData.comentario || 'Entrada de inventario',
          fecha: formData.fecha
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success('Entrada registrada correctamente');
          handleCloseModal();
          cargarEntradas();
        } else {
          toast.error(data.message);
        }
      } else {
        // Modo múltiple
        if (ingredientesAgregados.length === 0) {
          toast.error('Debe agregar al menos un ingrediente');
          return;
        }

        const ingredientesPayload = ingredientesAgregados
          .filter(i => i.cantidad > 0)
          .map(i => ({
            idIngrediente: i.idIngrediente,
            cantidad: i.cantidad
          }));

        if (ingredientesPayload.length === 0) {
          toast.error('Todos los ingredientes deben tener cantidad mayor a 0');
          return;
        }

        const payload = {
          tipo: 'crearEntradaInventarioMultiple',
          ingredientes: ingredientesPayload,
          comentario: formData.comentario || 'Entrada múltiple de inventario',
          fecha: formData.fecha
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`${data.data.ingredientesRegistrados} ingredientes registrados`);
          handleCloseModal();
          cargarEntradas();
        } else {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error('Error al guardar entrada:', error);
      toast.error('Error al guardar entrada');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarEntrada = (entrada) => {
    setEntradaAEliminar(entrada);
    setShowDeleteModal(true);
  };

  const eliminarEntrada = async () => {
    if (!entradaAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarEntradaInventario',
          idEntrada: entradaAEliminar.idEntrada
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Entrada eliminada correctamente');
        setShowDeleteModal(false);
        setEntradaAEliminar(null);
        cargarEntradas();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al eliminar entrada:', error);
      toast.error('Error al eliminar entrada');
    } finally {
      setGuardando(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIngredienteSeleccionado(null);
    setIngredientesAgregados([]);
    setModoSeleccion('simple');
    setFormData({
      cantidad: '',
      comentario: '',
      fecha: new Date().toISOString().split('T')[0]
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setEntradaAEliminar(null);
  };

  const verDetalleEntrada = (entrada) => {
    setEntradaDetalle(entrada);
    setShowDetalleModal(true);
  };

  const handleCloseDetalleModal = () => {
    setShowDetalleModal(false);
    setEntradaDetalle(null);
  };

  const calcularTotalEntrada = (ingredientes) => {
    return ingredientes.reduce((sum, ing) => {
      return sum + (parseFloat(ing.cantidad) * parseFloat(ing.costo || 0));
    }, 0);
  };

  if (loading && entradas.length === 0) {
    return (
      <div className="entrada-inventario-container">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="entrada-inventario-container">
      <Header titulo='Entrada de Inventario' color='#1565c0' />
      <div className="entrada-inventario-content">
        <div className="entrada-inventario-inner">
          <div className="entrada-inventario-header">
            <h1 className="entrada-inventario-titulo">Entradas de Inventario</h1>
            <div className="botones-header">
              <button onClick={nuevaEntrada} className="btn-nuevo">
                <FaPlus /> Nueva Entrada
              </button>
            </div>
          </div>

          <div className="panel-filtros">
            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Fecha Inicio:</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="filtro-item">
                  <label>Fecha Fin:</label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="btn-mostrar-entradas">
                  <button className="btn-buscar" onClick={cargarEntradas}>
                    Mostrar Entradas
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-listado">
            <div className="tabla-container">
              {loading ? (
                <div className="cargando">Cargando entradas...</div>
              ) : entradasAgrupadas.length === 0 ? (
                <div className="sin-datos">No se encontraron entradas en el periodo seleccionado.</div>
              ) : (
                <table className="tabla-entradas">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Comentario</th>
                      <th>Ingredientes</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entradasAgrupadas.map(entrada => (
                      <tr key={entrada.idEntrada}>
                        <td>{entrada.fecha}</td>
                        <td className="comentario-entrada">{entrada.comentario || '-'}</td>
                        <td className="cantidad-ingredientes">{entrada.ingredientes.length}</td>
                        <td>
                          <div className="acciones-entrada">
                            <button
                              onClick={() => verDetalleEntrada(entrada)}
                              className="btn-accion btn-ver"
                              title="Ver detalle"
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => confirmarEliminarEntrada(entrada)}
                              className="btn-accion btn-eliminar"
                              title="Eliminar entrada"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-entradas">
                <span>Total de registros: {entradasAgrupadas.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Modal para crear entrada */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Nueva Entrada de Inventario"
        onSave={guardarEntrada}
        saveLabel="Registrar"
        isSaving={guardando}
        size="xlarge"
        saveIcon={<FaBoxOpen />}
        saveButtonType="button"
      >
        {/* Selector de modo */}
        <div className="modo-selector">
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'simple' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('simple');
              setIngredientesAgregados([]);
            }}
          >
            Un Ingrediente
          </button>
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'multiple' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('multiple');
              setIngredienteSeleccionado(null);
            }}
          >
            Múltiples Ingredientes
          </button>
        </div>

        {modoSeleccion === 'simple' ? (
          <>
            <div className="form-group-entrada">
              <label>Ingrediente *</label>
              <Select
                value={ingredienteSeleccionado}
                onChange={handleIngredienteChange}
                options={opcionesIngredientes}
                placeholder="Buscar ingrediente..."
                isSearchable
                isClearable
                noOptionsMessage={() => "No se encontraron ingredientes"}
                className="select-producto"
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '42px',
                    borderColor: '#d1d5db',
                    '&:hover': { borderColor: '#1565c0' }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#1565c0' : state.isFocused ? '#f5f5f5' : 'white',
                    color: state.isSelected ? 'white' : '#333'
                  })
                }}
              />
            </div>

            <FormGrid columns={2} gap="16px">
              <FormField
                label="Cantidad"
                type="number"
                name="cantidad"
                value={formData.cantidad}
                onChange={(e) => {
                  const valor = limitarDecimales(e.target.value, 3);
                  setFormData(prev => ({ ...prev, cantidad: valor }));
                }}
                required
                placeholder="0.000"
                step="0.001"
                min="0"
              />

              <FormField
                label="Fecha"
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleInputChange}
                icon={<FaCalendarAlt />}
              />
            </FormGrid>
          </>
        ) : (
          <>
            <div className="ingredientes-section">
              <div className="agregar-ingrediente-row">
                <div className="select-ingrediente-container">
                  <Select
                    value={ingredienteSeleccionado}
                    onChange={setIngredienteSeleccionado}
                    options={opcionesIngredientes}
                    placeholder="Buscar ingrediente..."
                    isSearchable
                    isClearable
                    noOptionsMessage={() => "No se encontraron ingredientes"}
                    className="select-producto"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#d1d5db',
                        '&:hover': { borderColor: '#1565c0' }
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#1565c0' : state.isFocused ? '#f5f5f5' : 'white',
                        color: state.isSelected ? 'white' : '#333'
                      })
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-agregar-ingrediente"
                  onClick={agregarIngrediente}
                >
                  <FaPlus /> Agregar
                </button>
              </div>

              {ingredientesAgregados.length > 0 && (
                <div className="lista-ingredientes-agregados">
                  <label>Ingredientes a ingresar:</label>
                  <div className="tabla-ingredientes">
                    <div className="header-ingredientes">
                      <span className="col-nombre">Ingrediente</span>
                      <span className="col-cantidad">Cantidad</span>
                      <span className="col-acciones">Acciones</span>
                    </div>
                    {ingredientesAgregados.map(ing => (
                      <div key={ing.idIngrediente} className="row-ingrediente">
                        <span className="col-nombre">{ing.nombre}</span>
                        <span className="col-cantidad">
                          <input
                            type="number"
                            value={ing.cantidad}
                            onChange={(e) => cambiarCantidadIngrediente(ing.idIngrediente, e.target.value)}
                            min="0.001"
                            step="0.001"
                          />
                        </span>
                        <span className="col-acciones">
                          <button
                            type="button"
                            className="btn-eliminar-ingrediente"
                            onClick={() => eliminarIngredienteAgregado(ing.idIngrediente)}
                          >
                            <FaTrash />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <FormField
              label="Fecha"
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={handleInputChange}
              icon={<FaCalendarAlt />}
            />
          </>
        )}

        <FormField
          label="Comentario / Origen"
          type="textarea"
          name="comentario"
          value={formData.comentario}
          onChange={handleInputChange}
          placeholder="Ej: Transferencia de KAPITAL NORTE, Compra a proveedor, etc."
          icon={<FaClipboardList />}
        />
      </FormModal>

      {/* Modal de confirmacion para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarEntrada}
        title="Eliminar Entrada"
        message="¿Está seguro de eliminar esta entrada? Se revertirá el inventario. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />

      {/* Modal para ver detalle de entrada */}
      <FormModal
        isOpen={showDetalleModal}
        onClose={handleCloseDetalleModal}
        title="Detalle de Entrada"
        size="large"
        showCancel={false}
        saveLabel="Cerrar"
        onSave={handleCloseDetalleModal}
        saveButtonType="button"
      >
        {entradaDetalle && (
          <div className="detalle-entrada-modal">
            <div className="detalle-info">
              <p><strong>Fecha:</strong> {entradaDetalle.fecha}</p>
              <p><strong>Comentario:</strong> {entradaDetalle.comentario || '-'}</p>
            </div>
            <div className="detalle-ingredientes">
              <label>Ingredientes:</label>
              <table className="tabla-detalle-ingredientes">
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th>Cantidad</th>
                    <th>Costo Unit.</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {entradaDetalle.ingredientes.map(ing => (
                    <tr key={ing.idEntradaDetalle}>
                      <td>{ing.nombreIngrediente}</td>
                      <td className="cantidad-detalle">{parseFloat(ing.cantidad).toFixed(3)}</td>
                      <td className="costo-detalle">${parseFloat(ing.costo || 0).toFixed(2)}</td>
                      <td className="subtotal-detalle">
                        ${(parseFloat(ing.cantidad) * parseFloat(ing.costo || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="total-label">Total:</td>
                    <td className="total-valor">
                      ${calcularTotalEntrada(entradaDetalle.ingredientes).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </FormModal>
    </div>
  );
};

export default EntradaInventario;
